import gsap from "gsap";
import { CascadeStrategy } from "onearm";

/**
 * Cascade strategy tuned for Anubis reels.
 *
 * @extends CascadeStrategy
 */
export class AnubisCascadeStrategy extends CascadeStrategy {
    /**
     * @param {import("onearm").Reel} reel - Reel instance.
     * @param {Object} [options={}] - Strategy options.
     * @param {string} [options.fallEase] - Easing for symbols leaving the field.
     * @param {string} [options.dropEase] - Easing for symbols landing into final cells.
     * @param {string} [options.quickDropEase] - Easing for quick-stop symbol landing.
     */
    constructor(reel, options = {}) {
        super(reel, options);

        this.fallEase = options.fallEase ?? this.ease;
        this.dropEase = options.dropEase ?? this.ease;
        this.quickDropEase = options.quickDropEase ?? this.quickEase;
    }

    /**
     * Starts cascade animation for symbols leaving the field.
     *
     * @param {string} [type="normal"] - Spin type.
     * @returns {gsap.core.Timeline}
     */
    start(type = "normal") {
        this.isAnimating = true;
        const instant = type === "turbo";
        const timeline = gsap.timeline();
        const symbols = [...this.reel.children];

        symbols.sort((a, b) => b.y - a.y);

        symbols.forEach((symbol, i) => {
            timeline.to(
                symbol,
                {
                    y: symbol.y + this.reel.reelHeight,
                    duration: this.fallDuration,
                    ease: this.fallEase,
                    onComplete: () => {
                        this.reel.removeSymbol(symbol);
                    },
                },
                instant ? 0 : i * this.fallDelay
            );
        });

        return timeline;
    }

    /**
     * Drops all final symbols into place during quick stop.
     *
     * @param {Object} matrix - Symbol matrix with final positions.
     * @param {number} [delay=0] - Delay before animation starts.
     * @param {string} [spinType="normal"] - Spin type.
     * @returns {gsap.core.Timeline}
     */
    quickStop(matrix, delay = 0, spinType = "normal") {
        this.clearRemainingSymbols();

        const timeline = gsap.timeline();
        const symbolsData = [];

        matrix.forEachColumn(this.reel.column, (row, column, symbolData) => {
            if (symbolData) {
                symbolsData.push({ row, symbolData });
            }
        });

        symbolsData.sort((a, b) => b.row - a.row);

        let startRow = 0;
        symbolsData.forEach(({ row, symbolData }, i) => {
            startRow -= 1;

            const symbol = this.reel.addNewSymbol(symbolData, startRow);
            const targetY = row * this.reel.symbolHeight;
            const tl = gsap.timeline();

            if (spinType !== "turbo") {
                const dropAnimation = symbol.playDropAnimation(this.dropDuration);
                if (dropAnimation) {
                    tl.add(dropAnimation, 0.3);
                }
            }

            tl.to(
                symbol,
                {
                    y: targetY,
                    duration: this.quickDropDuration,
                    ease: this.quickDropEase,
                },
                0
            );

            timeline.add(tl, delay + i * this.quickFallDelay);
        });

        return timeline;
    }

    /**
     * Creates new symbols above the reel and drops them to final positions.
     *
     * @param {Object} matrix - Symbol matrix with final positions.
     * @param {string} [spinType="normal"] - Spin type.
     * @returns {gsap.core.Timeline}
     */
    fillFromTop(matrix, spinType = "normal") {
        const timeline = gsap.timeline();
        const symbolsData = [];

        matrix.forEachColumn(this.reel.column, (row, column, symbolData) => {
            if (symbolData) {
                symbolsData.push({ row, symbolData });
            }
        });

        symbolsData.sort((a, b) => b.row - a.row);

        let startRow = 0;

        symbolsData.forEach(({ row, symbolData }, i) => {
            startRow -= 1;

            const symbol = this.reel.addNewSymbol(symbolData, startRow);
            const targetY = row * this.reel.symbolHeight;
            const delay = i * this.fallDelay;
            const distance = targetY - symbol.y;
            const duration = this.dropAnimationDuration + (distance / this.reel.symbolHeight) * this.distanceFactor;
            const tl = gsap.timeline();

            if (spinType !== "turbo") {
                const dropAnimation = symbol.playDropAnimation();
                if (dropAnimation) {
                    tl.add(dropAnimation, 0);
                }
            }

            tl.to(
                symbol,
                {
                    y: targetY,
                    duration,
                    ease: this.dropEase,
                },
                0
            );

            timeline.add(tl, delay);
        });

        return timeline;
    }
}
